import React, { FunctionComponent, ReactNode, useState } from 'react'
import styled from 'styled-components'
import { CroppedText } from './cropped-text'
import { CheckoutPopup } from './checkout-popup'
import { useModal } from '../hooks/use-modal'
import classNames from 'classnames'
import { headEnText, headJaText } from '../styles/text-styles'

const Outer = styled.div`
  font-family: var(--sans-serif-font);
  font-weight: 400;
  @media (max-width: 60rem){
    
  }
`

const Inner = styled.div`
@media (max-width: 60rem){
  
}

`

const Label = styled.div`
font-size: 0.625rem;
line-height: 0.65;
padding: 0.25rem;
border: 1px solid currentColor;
width: fit-content;
@media (max-width: 60rem){
  
}
`

const NameEn = styled(CroppedText)`
${headEnText}
margin-top: 1.5rem;
@media (max-width: 60rem){
  
}
`

const NameJa = styled.div`
font-size: 1.171875rem;
line-height: 1.5;
letter-spacing: 0.05em;
margin-top: 0.5rem;
@media (max-width: 60rem){
  
}
`

const Description = styled(CroppedText)`
font-size: 0.75rem;
line-height: 1.5;
letter-spacing: 0.05em;
margin-top: 1.25rem;
@media (max-width: 60rem){
  
}
`

const TagContainer = styled.div`
display: grid;
grid-template-columns: 1fr 1fr;
gap: 1.125rem;
margin-top: 1.25rem;
@media (max-width: 60rem){
  
}
`

const Tag = styled.div`
${headJaText}
padding: 0.75rem;
background-color: #E9E3E0;
border-radius: 100rem;
text-align: center;
@media (max-width: 60rem){
  
}
`

const TagsNote = styled.div`
font-size: 0.625rem;
line-height: 1.5;
letter-spacing: 0.05em;
margin-top: 1.125rem;
@media (max-width: 60rem){
  
}
`

const Note = styled(CroppedText)`
font-size: 0.625rem;
line-height: 1.5;
letter-spacing: 0.05em;
margin-top: 3rem;
@media (max-width: 60rem){
  
}
`

const PriceContainer = styled.div`
  margin-top: 3.75rem;
  @media (max-width: 60rem){
    
  }
`

const PriceRow = styled.div`
  padding-block: 1rem;
  display: flex;
  flex-wrap: wrap;
  cursor: pointer;
  align-items: center;
  border-bottom: 1px solid color-mix(in srgb, currentColor, transparent);
  @media (max-width: 60rem){
    flex-direction: column;
  }
  & + & {
    margin-top: 1.875rem;
    @media (max-width: 60rem){
      
    }
  }
  &.disabled {
    pointer-events: none;
  }
`

const PriceLabel = styled.div`
  margin-right: auto;
  font-size: 0.9375rem;
line-height: 1.5;
letter-spacing: 0.05em;
line-height: 2;
@media (max-width: 60rem){
  
}
small {
  font-size: 0.75em;
  display: block;
}
`

const BuyButton = styled.div`
font-size: 0.9375rem;
line-height: 1.5;
letter-spacing: 0.15em;
padding: 1rem 1.5rem;
background-color: var(--brown);
white-space: nowrap;
transition: background-color 0.4s;
color: var(--inverted-text-color);
@media (max-width: 60rem){
  margin-top: 1rem;
  width: 100%;
  text-align: center;
  box-sizing: border-box;
  padding: 0.75rem;
}
  &:hover {
    background-color: color-mix(in srgb, #8F6B5A 50%, white);
  }
  &.disabled {
    background-color: #888;
    pointer-events: none;
  }
  &.pre-order {
    background-color: var(--orange);
  }
  ${PriceRow}.disabled & {
    pointer-events: none;
    opacity: 0.5;
  }
`

const VariantNote = styled.div`
  font-size: 0.8rem;
  margin-top: 0.75rem;
  flex: 0 0 100%;
  text-align: right;
`

const SubscriptionNotice = styled.div`
  font-size: 0.6875rem;
  line-height: 1.3636363636363635;
  font-weight: 500;
  font-family: var(--sans-serif-ja-font);
  margin-top: 1.5rem;
  @media (max-width: 60rem){
    
  }
  a {
    text-decoration: underline;
  }
`

export type ProductData = {
  label: string,
  nameEn: string,
  nameJa: string,
  description: string,
  tags: string[],
  tagsNote: string,
  note?: string,
  subscriptionNotice?: string
  variants: Variant[],
  soldOut?: boolean,
  preOrder?: boolean
}
type Variant = {
  id: string | number,
  label: string,
  buttonLabel: string,
  price: number,
  netContent: string,
  note?: string,
  subscriptionNotice?: ReactNode,
  disabled?: boolean
}

type ProductInfoProps = {
  data: ProductData
}
export const ProductInfo: FunctionComponent<ProductInfoProps> = ({data}) => {
  const [open, setOpen] = useState(false)
  const {open: openModal} = useModal()
  const [sending, setSending] = useState(false)
  const [selected, setSelected] = useState<number | string>()
  const addToCart = async (variantId: string | number) => {
    const data = {
      authenticity_token: document.querySelector<HTMLMetaElement>('[name="csrf-token"]')!.content,
      variant_id: variantId.toString(),
      quantity: '1'
    }

    const form = document.createElement('form')
    form.method = 'POST'
    form.action = `/shop/cart/add`
    for(const [key, value] of Object.entries(data)){
      const input = document.createElement('input')
      input.name = key
      input.value = value
      form.appendChild(input)
    }
    document.body.appendChild(form)
    form.submit()
    return
  }
  return <Outer>
    <Inner>
      <Label dangerouslySetInnerHTML={{__html: data.label}} />
      <NameEn dangerouslySetInnerHTML={{__html: data.nameEn}} />
      <NameJa dangerouslySetInnerHTML={{__html: data.nameJa}} />
      <Description dangerouslySetInnerHTML={{__html: data.description}} />
      <TagContainer>
        {data.tags.map(tag => {
          return <Tag dangerouslySetInnerHTML={{__html: tag}} />
        })}
      </TagContainer>
      <TagsNote dangerouslySetInnerHTML={{__html: data.tagsNote}} />
      <PriceContainer>
        {data.variants.map((variant, i) => {
          return <PriceRow key={i} className={classNames({disabled: data.soldOut || variant.disabled})} onClick={() => {
            setSelected(variant.id)
            setOpen(true)
          }}>
            <PriceLabel dangerouslySetInnerHTML={{__html: variant.label}} />
            <BuyButton dangerouslySetInnerHTML={{__html: variant.buttonLabel}} />
            {
              variant.note && <VariantNote>{variant.note}</VariantNote>
            }
          </PriceRow>
        })}
      </PriceContainer>
      {
        data.note && <Note dangerouslySetInnerHTML={{__html: data.note}} />
      }
    </Inner>
    <CheckoutPopup
      data={data}
      open={open}
      onClose={() => {
        setOpen(false)
        setSelected(undefined)
      }}
      defaultSelect={selected}
      onClick={addToCart}
    />
  </Outer>
}